import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import ThreadPostEditor from './ThreadPostEditor';
import ImageGalleryDialog from './ImageGalleryDialog';

const PlatformContainer = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    overflow-y: scroll;
    height: ${props => props.hasSelectedAccounts ? '50vh' : '100%'};
`;

const PLATFORM_LIMITS = {
    twitter: 280,
    bluesky: 300
};

/**
 * ThreadComposer component for creating multi-post threads
 * Supports Twitter/X and BlueSky platforms with platform-specific validation
 * Now includes media support for each individual post
 */
const ThreadComposer = forwardRef(({ selectedAccounts, onThreadChange, initialThread = null, onImageGalleryOpen }, ref) => {
    const [threadPosts, setThreadPosts] = useState(
        initialThread && initialThread.length > 0 
            ? initialThread.map(post => ({
                text: post.text || '',
                media_url: post.media_url || '',
                media_type: post.media_type || '',
                alt_text: post.alt_text || '',
                media_file: post.media_file || null
            }))
            : [{ text: '', media_url: '', media_type: '', alt_text: '', media_file: null }]
    );

    useEffect(() => {
        // Clean up function to revoke object URLs when component unmounts
        return () => {
            threadPosts.forEach(post => {
                if (post.media_url && post.media_url.startsWith('blob:')) {
                    URL.revokeObjectURL(post.media_url);
                }
            });
        };
    }, []);

    useEffect(() => {
        // Transform thread posts for parent component
        const transformedPosts = threadPosts.map(post => ({
            text: post.text,
            media_url: post.media_url,
            media_type: post.media_type,
            alt_text: post.alt_text,
            media_file: post.media_file
        }));
        onThreadChange(transformedPosts);
    }, [threadPosts, onThreadChange]);

    /**
     * Add a new post to the thread
     */
    const addPost = () => {
        if (threadPosts.length < 25) { // Twitter's thread limit
            setThreadPosts([...threadPosts, { 
                text: '', 
                media_url: '', 
                media_type: '', 
                alt_text: '',
                media_file: null 
            }]);
        }
    };

    /**
     * Remove a post from the thread
     * @param {number} index - Index of post to remove
     */
    const removePost = (index) => {
        if (threadPosts.length > 1) {
            // Clean up object URL if it exists
            const postToRemove = threadPosts[index];
            if (postToRemove.media_url && postToRemove.media_url.startsWith('blob:')) {
                URL.revokeObjectURL(postToRemove.media_url);
            }
            
            const newPosts = threadPosts.filter((_, i) => i !== index);
            setThreadPosts(newPosts);
        }
    };

    /**
     * Update text content of a specific post
     * @param {number} index - Post index
     * @param {string} text - New text content
     */
    const updatePostText = (index, text) => {
        const newPosts = [...threadPosts];
        newPosts[index].text = text;
        setThreadPosts(newPosts);
    };

    /**
     * Update media content of a specific post
     * @param {number} index - Post index
     * @param {Object} mediaData - Media data {url, type, file}
     */
    const updatePostMedia = (index, mediaData) => {
        const newPosts = [...threadPosts];
        const currentPost = newPosts[index];
        
        // Clean up previous object URL if it exists
        if (currentPost.media_url && currentPost.media_url.startsWith('blob:')) {
            URL.revokeObjectURL(currentPost.media_url);
        }
        
        newPosts[index] = {
            ...currentPost,
            media_url: mediaData.url || '',
            media_type: mediaData.type || '',
            media_file: mediaData.file || null
        };
        setThreadPosts(newPosts);
    };

    /**
     * Update alt text of a specific post
     * @param {number} index - Post index
     * @param {string} altText - New alt text
     */
    const updatePostAltText = (index, altText) => {
        const newPosts = [...threadPosts];
        newPosts[index].alt_text = altText;
        setThreadPosts(newPosts);
    };

    /**
     * Get character limit for selected platforms
     * @returns {number} Minimum character limit across selected platforms
     */
    const getCharacterLimit = () => {
        const platformLimits = selectedAccounts
            .map(account => PLATFORM_LIMITS[account.platform.toLowerCase()])
            .filter(limit => limit !== undefined);
        
        return platformLimits.length > 0 ? Math.min(...platformLimits) : 280;
    };

    /**
     * Check if any post exceeds character limits
     * @returns {boolean} True if any post exceeds limits
     */
    const hasCharacterLimitExceeded = () => {
        const limit = getCharacterLimit();
        return threadPosts.some(post => post.text.length > limit);
    };

    /**
     * Get platform-specific validation errors
     * @returns {Array} Array of validation error messages
     */
    const getValidationErrors = () => {
        const errors = [];
        const limit = getCharacterLimit();
        
        if (threadPosts.length < 2) {
            errors.push('Thread must contain at least 2 posts');
        }
        
        if (threadPosts.length > 25) {
            errors.push('Thread cannot exceed 25 posts');
        }
        
        threadPosts.forEach((post, index) => {
            if (post.text.trim() === '') {
                errors.push(`Post ${index + 1} cannot be empty`);
            }
            if (post.text.length > limit) {
                errors.push(`Post ${index + 1} exceeds ${limit} character limit`);
            }
        });
        
        return errors;
    };

    const characterLimit = getCharacterLimit();
    const validationErrors = getValidationErrors();

    useImperativeHandle(ref, () => ({
        updatePostMedia
    }));

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                    Thread Composer ({threadPosts.length} posts)
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={addPost}
                    disabled={threadPosts.length >= 25}
                    variant="outlined"
                    size="small"
                >
                    Add Post
                </Button>
            </Box>

            {/* Platform Info */}
            <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Selected Platforms:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedAccounts.map(account => (
                        <Chip
                            key={account.userId}
                            label={`${account.platform} (${PLATFORM_LIMITS[account.platform.toLowerCase()] || 'N/A'} chars)`}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Stack>
            </Box>

            {/* Thread Posts Container */}
            <PlatformContainer hasSelectedAccounts={threadPosts.length > 0}>
                {threadPosts.map((post, index) => (
                    <ThreadPostEditor
                        key={index}
                        post={post}
                        index={index}
                        characterLimit={characterLimit}
                        canRemove={threadPosts.length > 1}
                        onUpdateText={updatePostText}
                        onUpdateMedia={updatePostMedia}
                        onUpdateAltText={updatePostAltText}
                        onRemovePost={removePost}
                        onImageGalleryOpen={onImageGalleryOpen}
                        selectedAccounts={selectedAccounts}
                        isLast={index === threadPosts.length - 1}
                    />
                ))}
            </PlatformContainer>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
                <Box mt={2}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                        Please fix the following issues:
                    </Typography>
                    {validationErrors.map((error, index) => (
                        <Typography key={index} variant="body2" color="error">
                            • {error}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
});

export default ThreadComposer; 